import { useCallback, useState } from 'react';
import {
  Collapse,
  Flex,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  InputProps,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { FieldPath, FieldPathValue, FieldValues, Validate, ValidationRule, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFormErrorMessage } from './useFormErrorMessage';
import { isRequiredValueMissing } from './requiredValidation';

export interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormControlProps {
  name: TFieldName;
  label: string;
  description?: React.ReactNode;
  type?: React.HTMLInputTypeAttribute;
  validate?: Validate<FieldPathValue<TFieldValues, TFieldName>, TFieldValues>;
  pattern?: ValidationRule<RegExp>;
  autoComplete?: InputProps['autoComplete'];
  placeholder?: string;
  isCollapsible?: boolean;
}

export function FormInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  type,
  isRequired,
  validate,
  pattern,
  autoFocus,
  size,
  autoComplete,
  placeholder,
  isCollapsible,
  ...rest
}: FormInputProps<TFieldValues>) {
  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>();
  const { t } = useTranslation('forms');

  const [isExpanded, setIsExpanded] = useState(false);
  const errorMessage = useFormErrorMessage(name, label, errors);

  const requiredValidate = useCallback(
    (value: unknown) =>
      !isRequired || !isRequiredValueMissing(value) || t('form.validationError.required', { field: label }),
    [isRequired, label, t]
  );

  return (
    <FormControl isRequired={isRequired} isInvalid={!!errors[name]} {...rest}>
      <Flex justifyContent="space-between" alignItems="center" mb="2">
        <FormLabel size={size} mb="0">
          {label}
        </FormLabel>
        {isCollapsible && !isExpanded && (
          <IconButton
            aria-label={`Add ${label}`}
            icon={<AddIcon />}
            size="xs"
            variant="ghost"
            onClick={() => setIsExpanded(true)}
            background="primary.100"
            color="primary"
          />
        )}
      </Flex>
      <Collapse in={isExpanded || !isCollapsible} animateOpacity>
        <Input
          type={type}
          required={false}
          autoFocus={isCollapsible || autoFocus}
          autoComplete={autoComplete}
          placeholder={placeholder}
          size={size}
          {...register(name, {
            // A custom validator owns required-ness entirely once supplied (e.g. cross-field
            // "either this or that" rules), so it must not be preempted by the generic check.
            validate: validate ?? requiredValidate,
            pattern,
          })}
        />
        <FormErrorMessage>{errorMessage}</FormErrorMessage>
        <FormHelperText>{description}</FormHelperText>
      </Collapse>
    </FormControl>
  );
}
